import LayeredAutocompleteSelect from "./LayeredAutocompleteSelect";
import { compareMessages } from "../shared/utils/messageOrder";
import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Link as MuiLink,
  MenuItem,
  TextField,
  Badge,
  Chip,
  Menu,
} from "@mui/material";
import theoLogo from "assets/img/theo_link.svg";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AddTaskOutlinedIcon from "@mui/icons-material/AddTaskOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import { NEUTRAL, VIOLET } from "../styles/brandTokens";
import ConnectionNotice from "./ticketToolbar/ConnectionNotice";
import ClearChatButton from "./ticketToolbar/ClearChatButton";
import {
  TOOLBAR_GROUP_SX,
  toolbarButtonSx,
  toolbarIconSize,
  toolbarStandaloneButtonSx,
} from "./ticketToolbar/toolbarStyles";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SettingsIcon from "@mui/icons-material/Settings";
import Tooltip from "@mui/material/Tooltip";
import ToolAuditLogPopup from "./ToolAuditLogPopup";
import LiveDebugPanel from "./LiveDebugPanel";
import { useLiveDebug } from "../hooks/useLiveDebug";
import UndoActionsModal from "./UndoActionsModal";
import { chatApiInstance, type LinearTaskAssignee } from "../apis/chatApi";
import ChatBubble from "./ChatBubble";
import {
  buildReportHref,
  extractResearchJobId,
  ResearchReportJobStatusInline,
} from "../shared/components/ResearchReportJobStatusInline";
import {
  parseReportAttachmentsFromContent,
  stripReportAttachmentTokensForDisplay,
} from "../shared/utils/reportAttachmentTokens";
import { useChatHook } from "hooks/chatHook";
import TicketChatModeSelector from "./TicketChatModeSelector";
import AutoModeBadge from "./AutoModeBadge";
import { useTicketChatMode } from "../hooks/useTicketChatMode";
import { useThreadAgentId } from "../hooks/useThreadAgentId";
import { useAgents } from "../hooks/useAgents";
import LangSmithThreadLinkButton from "./LangSmithThreadLinkButton";
import { canShowTicketChatModeSelector, selectableAgents } from "../utils/ticketChatMode";
import type { AutomationCapacity } from "../types/integration";
import { formatTimestamp, cleanContent } from "../utils/chatUtils";
import { isTicketConversationReady } from "../utils/ticketConversationReady";
import { resolveCustomerConnectedToTheo } from "../utils/customerConnectedToTheo";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";
import { useAppDispatch, useAppSelector } from "../hooks/storeHook";
// import { fetchPills } from "../store/action";
import CloseIcon from "@mui/icons-material/Close";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { ChatAction } from "../actions/chatAction";
import { ActionTypes } from "../store/actionTypes";
import {
  AiOverview,
  DeleteModal,
  NeoButton,
  TicketResolverStateChip,
  TypingIndicator,
  type OverviewFetchResult,
} from "../shared/components";
import { integrationApiInstance } from "../apis/integrationApi";
import { useNavigate } from "react-router-dom";
import RemoteSessionInputSection from "./RemoteSessionInputSection";
import type { ChatSendPayload } from "./InputBox";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
// import PillsBar from "./PillsBar";
import ThinkingIndicator from "./ThinkingIndicator";
import Typeahead from "./Typeahead";
import { useAuth0 } from "@auth0/auth0-react";
import { useUserMsp } from "../hooks/useUserMsp";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import useComputerUseChatDisabledReason from "../hooks/useComputerUseChatDisabledReason";
import useThreadWait, { composeChatDisabledReason, deleteThreadWarning } from "../hooks/useThreadWait";
import useChatReplyPlaceholder from "../hooks/useChatReplyPlaceholder";
import useThreadAutomationBadge, { threadAutomationBadgeMessageId } from "../hooks/useThreadAutomationBadge";
import useComputerUseRuns from "../hooks/useComputerUseRuns";
import useTicketResolverState from "../hooks/useTicketResolverState";
import { BEFORE_FIRST_MESSAGE } from "../utils/computerUseRunPlacement";
import WorkflowApi, { Workflow } from "../apis/workflowApi";
import { isCustomWorkflowsNavVisible } from "../utils/customWorkflowsAccess";
import {
  userLifecycleFromTicketRecordHeuristic,
  type UserLifecycle,
} from "../utils/ticketLifecycleClassification";
import { isHorizonRootRole } from "../utils/horizonRoles";
import { canAccessAnalyticsAssistant } from "../utils/analyticsAssistantAccess";
import {
  customWorkflowMatchesTicketLifecycle,
  lifecycleToCustomWorkflowType,
} from "../utils/customWorkflowLifecycleMatch";
import { getFontSizes } from '../utils/fontSizeUtils';
import { collapseStreamingChunks, groupConsecutivePlanSteps, normalizeMessages } from "../shared/utils/messageUtils";
import {
  agentDisplayNameForMessage,
  getChatBubblePresentation,
  isNeoAssistantAuthor,
} from "../shared/utils/messageSenderLabels";
import { stripLeadingWorkflowStepNumber } from "../utils/stripLeadingWorkflowStepNumber";
import SupervisorApi from "../apis/supervisorApi";
import CustomWorkflowApi from "../apis/customWorkflowApi";
import ScheduleApi, { TheoSchedule, TheoScheduleAction } from "../apis/scheduleApi";
import { isSchedulerLdEnabled } from "../utils/schedulerAccess";
import { formatDateTimeInTimeZone, getDefaultTimeZone } from "../shared/utils/timezoneUtils";
import {
  isPendingStructuredWorkflowResumeSchedule,
  isStructuredWorkflowResumeSchedule,
  isStructuredWorkflowStartSchedule,
  scheduleActionConfig,
  sortSchedulesNewestFirst,
  structuredWorkflowResumeAction,
  workflowStartDisplayName,
} from "../shared/utils/scheduleKinds";
import ScheduleWorkflowDialog from "../shared/components/ScheduleWorkflowDialog";
import { applyWorkflowStepDelayOverridesToSteps } from "../shared/utils/workflowScheduleSteps";


function scheduleStatusDotColor(status?: string | null) {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "completed" || normalized === "succeeded") return "#16A34A";
  if (normalized === "failed") return "#DC2626";
  if (normalized === "running") return "#D97706";
  if (normalized === "canceled") return "#6B7280";
  return "#2563EB";
}

function scheduleStatusLabel(status?: string | null) {
  const normalized = String(status || "scheduled").toLowerCase();
  return normalized.replace(/_/g, " ");
}

function stripWorkflowResumePrefix(value: unknown) {
  return String(value || "")
    .replace(/^Resume workflow step:\s*/i, "")
    .trim();
}

function workflowScheduledWorkLabel(action?: TheoScheduleAction | null) {
  if (!action) return "";
  const config = scheduleActionConfig(action);
  const label =
    stripWorkflowResumePrefix(config.phase_label) ||
    stripWorkflowResumePrefix((config.resume_payload as any)?.phase_label) ||
    stripWorkflowResumePrefix(action.action_description);
  return stripLeadingWorkflowStepNumber(label);
}

function scheduleDropdownTitle(schedule: TheoSchedule) {
  if (isStructuredWorkflowStartSchedule(schedule)) {
    return workflowStartDisplayName(schedule);
  }
  const workflowAction = structuredWorkflowResumeAction(schedule);
  if (!workflowAction) return schedule.title || "Untitled schedule";
  const config = scheduleActionConfig(workflowAction);
  const workflowName =
    String(config.workflow_name || (config.resume_payload as any)?.workflow_name || "").trim() ||
    String(schedule.title || "").split(":")[0].trim() ||
    "Workflow";
  const stepLabel = workflowScheduledWorkLabel(workflowAction);
  return stepLabel ? `${workflowName}: ${stepLabel}` : schedule.title || workflowName;
}

function scheduleWorkItemCountLabel(schedule: TheoSchedule) {
  const count = schedule.actions?.length || 0;
  return `${count} action${count === 1 ? "" : "s"}`;
}

function scheduleDropdownStatusText(schedule: TheoSchedule) {
  const status = scheduleStatusLabel(schedule.status);
  if (isStructuredWorkflowResumeSchedule(schedule) || isStructuredWorkflowStartSchedule(schedule)) {
    return status;
  }
  return `${status} · ${scheduleWorkItemCountLabel(schedule)}`;
}

/** Human-readable step title for chat and UI; mirrors Custom Workflow template labels where possible. */
function workflowActionDisplayLabel(action: string): string {
  const a = String(action || "");
  switch (a) {
    case "verify_authorized_requester":
      return "Verify authorized requester";
    case "create_user":
    case "m365_create_new_user":
      return "Create user";
    case "m365_reset_password":
      return "Reset password";
    case "m365_update_contact_info":
      return "Set contact info";
    case "m365_update_organizational_info":
      return "Set org info";
    case "m365_update_personal_information":
      return "Set personal info";
    case "m365_assign_license_to_user":
      return "Assign license";
    case "m365_set_email_signature":
      return "Set email signature";
    case "m365_add_user_to_group":
      return "Assign groups";
    case "m365_add_owner_to_group":
      return "Transfer team / group ownership";
    case "m365_set_manager":
      return "Set manager";
    case "m365_add_user_to_team":
      return "Add to Microsoft Team";
    case "add_to_sharepoint_site":
    case "m365_grant_site_access":
      return "Assign SharePoint site";
    case "m365_send_email":
    case "send_email":
    case "compose_email":
      return "Compose email";
    case "itglue_create_password":
      return "IT Glue: save user password";
    case "m365_block_unblock_user":
      return "Block sign-in";
    case "m365_revoke_user_session":
      return "Revoke sign-in sessions";
    case "m365_get_user_auth_details":
      return "List MFA / auth methods";
    case "m365_get_user_mfa_registration_status":
      return "Check MFA registration";
    case "m365_list_user_authentication_methods":
      return "List auth methods";
    case "m365_get_user_signin_mfa_details":
      return "Check sign-in MFA";
    case "m365_add_user_phone_authentication_method":
      return "Add MFA phone";
    case "m365_update_user_phone_authentication_method":
      return "Update MFA phone";
    case "m365_delete_authentication_method":
      return "Remove authentication methods";
    case "m365_set_mailbox_settings":
      return "Set automatic replies (OOO)";
    case "m365_set_forwarding_settings":
      return "Set mail forwarding";
    case "m365_grant_onedrive_access":
      return "Grant OneDrive access";
    case "m365_list_user_groups":
      return "List group memberships";
    case "m365_remove_user_from_group":
      return "Remove from group";
    case "m365_remove_license_from_user":
      return "Remove Microsoft 365 licenses";
    case "m365_delete_user":
      return "Delete user";
    case "itglue_create_document":
      return "IT Glue: create document";
    default: {
      const spaced = a.replace(/_/g, " ").trim();
      if (!spaced) return "Step";
      return spaced.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    }
  }
}

/** When DB workflow_type is missing or defaulted wrong, infer from step actions/ids. */
function inferWorkflowTypeFromStepList(steps: { action?: string; id?: string }[]): "User Offboarding" | null {
  for (const s of steps) {
    const action = String(s?.action || "");
    const sid = String(s?.id || "");
    if (action === "m365_block_unblock_user" || action === "m365_revoke_user_session") return "User Offboarding";
    if (sid.startsWith("offboard_")) return "User Offboarding";
    if (action === "m365_get_user_auth_details" || action === "m365_delete_authentication_method") {
      return "User Offboarding";
    }
  }
  return null;
}

function stepTitleForTechnicianSummary(s: { action?: string; label?: string }): string {
  const action = String(s.action || "");
  const stripped = stripLeadingWorkflowStepNumber(String(s.label || "").trim());
  const slugFromAction = action.replace(/_/g, " ").toLowerCase();
  const strippedLower = stripped.toLowerCase();
  if (action === "m365_reset_password" && strippedLower === "set password") {
    return "Reset password";
  }
  if (
    action === "m365_remove_license_from_user" &&
    ["remove license", "remove licenses"].includes(strippedLower)
  ) {
    return "Remove Microsoft 365 licenses";
  }
  // Saved label missing, or stored value is just the action slug — use friendly map
  if (
    !stripped ||
    strippedLower === slugFromAction ||
    /^[a-z0-9_]+$/i.test(stripped)
  ) {
    return workflowActionDisplayLabel(action);
  }
  return stripped;
}

/** Short message shown in chat; full tool/phase instructions come from structured_workflow on the server. */
function buildTechnicianWorkflowChatSummary(params: {
  workflowName: string;
  workflowType?: string;
  steps: { action?: string; label?: string }[];
}): string {
  const { workflowName, workflowType, steps } = params;
  let effectiveType = workflowType;
  const inferred = inferWorkflowTypeFromStepList(steps);
  if (inferred === "User Offboarding" && (effectiveType === "User Onboarding" || !effectiveType)) {
    effectiveType = "User Offboarding";
  }
  const lines: string[] = [];
  lines.push(`Run workflow: ${workflowName}`);
  if (effectiveType === "User Offboarding") {
    lines.push("Type: User offboarding");
  } else if (effectiveType === "User Onboarding") {
    lines.push("Type: User onboarding");
  }
  lines.push("");
  lines.push("Steps:");
  for (let i = 0; i < steps.length; i++) {
    lines.push(`${i + 1}. ${stepTitleForTechnicianSummary(steps[i])}`);
  }
  lines.push("");
  lines.push(
    "First, the assistant runs a short **plan phase**: it checks M365 / IT Glue (groups, sites, licenses, etc.) using read-only tools, summarizes what is ready or missing, then continues with the steps above."
  );
  if (effectiveType === "User Offboarding") {
    lines.push("");
    lines.push(
      "Note: When steps 1–2 are present (block sign-in and revoke sessions), run those first in that order. Some steps may require Exchange Admin Center, Entra Enterprise Applications, or Teams Admin for tasks that cannot be automated here."
    );
  } else if (effectiveType === "User Onboarding") {
    lines.push("");
    lines.push(
      "Note: When contact, org, and personal profile steps appear together, they may be combined into a single approval."
    );
  }
  return lines.join("\n");
}

interface Message {
  message_id: string;
  ai_thread_id: string;
  user_id: string;
  content: string;
  content_type: string;
  created_at: { seconds: string; nanos: number };
  automation_capacity?: AutomationCapacity;
  /** The agent a technician picked when sending this message. */
  agent_id?: number;
  pending?: boolean;
  isStreaming?: boolean;
  status?: 'pending' | 'sent' | 'confirmed' | 'failed';
  error?: string;
  serverMessageId?: string;
  requestId?: string;
  /** Set when this message belongs to an agent run. */
  agent_display_name?: string | null;
  human_review?: {
    tool_name?: string | string[];
    tool_description?: string | string[];
    args_json?: string | object | (string | object)[];
    steps_taken?: string[];
    plan_name?: string | string[];
    plan_description?: string | string[];
    plan_steps?: string[] | string[][];
    plan_url?: string | string[];
    information_request?: Array<{
      id: string;
      question: string;
    }>;
    information_answers?: Record<string, string>;
  };
  human_review_status?: string;
  human_review_response?: string;
  isHistory?: boolean;
  plan_progress?: {
    plan_description?: string;
    completed_step_indices: number[];
    total_steps: number;
    messages?: string[];
  };
  entry_id?: string;
}

interface NeoAssistantProps {
  ticketId: string | null;
  ticketDisplayId: string | null;
  userId: string;
  currentUser: any;
  customerId?: string | null;
  aiThreadId?: string | null;
  initialMessages?: any[];
  isEmbedded?: boolean;
  /**
   * When true, the chat shell fills the parent height (e.g. resizable panel / split view)
   * instead of using a viewport-based calc height. Keeps header + input fixed; only the
   * message list scrolls.
   */
  fillAvailableHeight?: boolean;
  hideHeader?: boolean;
  headerRightAction?: React.ReactNode;
  ticketTitle?: string | null;
  ticketLink?: string | null;
  /** Text for AI Overview (subject, description, etc.); fetched async from cortex via Horizon. */
  aiOverviewTicketContext?: string | null;
  /** Raw ticket object for onboarding/offboarding heuristic fallback when AI Overview omits lifecycle */
  ticketPayloadForLifecycle?: Record<string, unknown> | null;
  /** Optional prefill message for the input box (e.g., deep links from ticket notes). */
  prefillMessage?: string | null;
  /** Use the provided thread without creating the default user/ticket thread. */
  skipAutoInitialize?: boolean;
  /** Empty-state prompts, overridden by device/chat surfaces that are not ticket-backed. */
  nullStatePrompts?: string[];
  /** Ticket-only overview can be hidden by non-ticket surfaces that reuse the assistant shell. */
  showAiOverview?: boolean;
  /** Notifies embedded surfaces when the graph assigns a persisted conversation title. */
  onThreadName?: (threadName: string) => void;
}

interface PromptsListProps {
  suggestions: string[];
  onSelect: (text: string) => void;
  onClose: () => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  fontSizes?: { header: number; body: number; small: number };
}

/** Inline prompts overlay (used when pills bar is enabled). Exported to avoid unused lint while pills are disabled. */
export const PromptsList: React.FC<PromptsListProps> = ({
  suggestions,
  onSelect,
  onClose,
  fontSizes = { header: 18, body: 14, small: 12 },
}) => (
  <Box
    sx={{
      width: "100%",
      background: "#F5F5F5",
      border: "1px solid #E5E7EB",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      minWidth: 0,
      maxHeight: "400px",
    }}
  >
    {/* Header with close button */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: 2.5,
        py: 2,
        borderBottom: "1px solid #E5E7EB",
        background: "#F9FAFB",
        flexShrink: 0,
      }}
    >
      <Typography
        sx={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: `${fontSizes.header}px`,
          color: "#5A5E73",
          lineHeight: "20px",
          letterSpacing: "-0.01em",
        }}
      >
        Prompts
      </Typography>
      <IconButton aria-label="Close prompts" onClick={onClose}>
        <CloseIcon sx={{ fontSize: 22, color: "#5A5E73" }} />
      </IconButton>
    </Box>
    {/* Suggestions list with custom scrollbar */}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: "auto",
        background: "#F5F5F5",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#414141",
          borderRadius: "999px",
          border: "2px solid #414141",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
          margin: "4px 0",
        },
        scrollbarWidth: "thin",
        scrollbarColor: "#414141 transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          "& > *:not(:last-child)": {
            borderBottom: "1px solid #F3F4F6",
          },
        }}
      >
        {suggestions.map((s, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 2.5,
              py: 2,
              minHeight: 48,
              cursor: "pointer",
              background: "#FFFFFF",
              transition: "all 0.15s ease",
              "&:hover": {
                background: "#F9FAFB",
              },
              "&:active": {
                background: "#F3F4F6",
              },
            }}
            onClick={() => onSelect(s)}
          >
            <SearchIcon
              sx={{
                color: "#9CA3AF",
                fontSize: 16,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: `${fontSizes.body}px`,
                color: "#4B5563",
                fontWeight: 450,
                fontFamily: "Inter, sans-serif",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                lineHeight: "20px",
                letterSpacing: "-0.01em",
              }}
            >
              {s}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const NeoAssistant: React.FC<NeoAssistantProps> = ({
  ticketId,
  ticketDisplayId,
  userId,
  currentUser,
  customerId,
  aiThreadId: propAiThreadId,
  initialMessages,
  isEmbedded = false,
  fillAvailableHeight = false,
  hideHeader = false,
  headerRightAction,
  ticketTitle = null,
  ticketLink = null,
  aiOverviewTicketContext = null,
  ticketPayloadForLifecycle = null,
  prefillMessage = null,
  skipAutoInitialize = false,
  nullStatePrompts: nullStatePromptsProp,
  showAiOverview = true,
  onThreadName,
}) => {
  // Calculate font sizes based on embedded state
  const fontSizes = getFontSizes(isEmbedded);
  const useParentHeight = isEmbedded || fillAvailableHeight;
  const [inputMessage, setInputMessage] = useState("");
  // const [selectedPill, setSelectedPill] = useState<string | null>(null);
  // const [showPrompts, setShowPrompts] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [auditLogPopupOpen, setAuditLogPopupOpen] = useState(false);
  const [liveDebug, setLiveDebug] = useLiveDebug();
  const [undoModalOpen, setUndoModalOpen] = useState(false);
  const [undoPreSelected, setUndoPreSelected] = useState<string[]>([]);
  const openUndoModalWithPreSelection = (ids: string[]) => {
    setUndoPreSelected(ids);
    setUndoModalOpen(true);
  };
  const [showDeleteSnackbar, setShowDeleteSnackbar] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Typeahead state
  const [showTypeahead, setShowTypeahead] = useState(false);
  const [typeaheadDebounceTimer, setTypeaheadDebounceTimer] = useState<NodeJS.Timeout | null>(null);
  const [hasNavigatedToTypeahead, setHasNavigatedToTypeahead] = useState(false);
  const [debouncedInputText, setDebouncedInputText] = useState('');
  // Workflow state
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowDialogLifecycleFilter, setWorkflowDialogLifecycleFilter] = useState<
    "onboarding" | "offboarding" | null
  >(null);
  const [workflowToSchedule, setWorkflowToSchedule] = useState<Workflow | null>(null);
  const [workflowScheduleSubmitting, setWorkflowScheduleSubmitting] = useState(false);
  const [workflowScheduleError, setWorkflowScheduleError] = useState<string | null>(null);
  const [deletePendingScheduleCount, setDeletePendingScheduleCount] = useState(0);
  const [cancelingScheduleId, setCancelingScheduleId] = useState<string | null>(null);
  const [workflowScheduleSnackbar, setWorkflowScheduleSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  // Linear file-a-task state
  const [showFileLinearTaskDialog, setShowFileLinearTaskDialog] = useState(false);
  const [fileLinearTaskTitle, setFileLinearTaskTitle] = useState("");
  const [fileLinearTaskDescription, setFileLinearTaskDescription] = useState("");
  const [fileLinearTaskTicketLink, setFileLinearTaskTicketLink] = useState("");
  const [fileLinearTaskTraceUrl, setFileLinearTaskTraceUrl] = useState("");
  const [fileLinearTaskAssignee, setFileLinearTaskAssignee] = useState("");
  const [fileLinearTaskAssignees, setFileLinearTaskAssignees] = useState<LinearTaskAssignee[]>([]);
  const [fileLinearTaskAssigneesLoading, setFileLinearTaskAssigneesLoading] = useState(false);
  const [fileLinearTaskTraceLoading, setFileLinearTaskTraceLoading] = useState(false);
  const [fileLinearTaskSubmitting, setFileLinearTaskSubmitting] = useState(false);
  const [createdLinearTask, setCreatedLinearTask] = useState<{
    identifier: string;
    title: string;
    url: string;
  } | null>(null);
  const [fileLinearTaskSnackbar, setFileLinearTaskSnackbar] = useState<{
    open: boolean;
    severity: "success" | "error";
    message: string;
  }>({ open: false, severity: "success", message: "" });
  // Optimistic: ticket chat is enabled for every imported ticket. The
  // /chat-enabled check can still flip this to false; do not block the
  // conversation pane on that round trip.
  const [chatEnabled, setChatEnabled] = useState<boolean | null>(true);
  const [checkingChatEnabled, setCheckingChatEnabled] = useState(false);
  // Productivity apps status
  const [hasProductivityApps, setHasProductivityApps] = useState<boolean | null>(null);
  const ticketDetailsKnown =
    ticketPayloadForLifecycle != null ||
    Boolean(String(aiOverviewTicketContext ?? "").trim());
  const customerConnectedToTheo = resolveCustomerConnectedToTheo({
    ticketId,
    customerId,
    ticketDetailsKnown,
  });
  const dispatch = useAppDispatch();
  const supervisorApi = React.useMemo(() => new SupervisorApi(), []);
  const customWorkflowApi = React.useMemo(() => new CustomWorkflowApi(), []);
  const scheduleApi = React.useMemo(() => new ScheduleApi(), []);
  const flags = useFeatureFlag();
  const { user } = useAuth0();
  const { userMspInfo } = useUserMsp();
  const canUseDeepResearch = canAccessAnalyticsAssistant(flags as Record<string, unknown>, userMspInfo?.role);
  const schedulerEnabled = isSchedulerLdEnabled(flags as Record<string, unknown>);
  const isRootUser = isHorizonRootRole(userMspInfo?.role);
  const canFileLinearTask = isRootUser;
  const canOpenLangSmith = isRootUser;
  const showTicketChatModeSelector = canShowTicketChatModeSelector(ticketId);
  const canUseCustomWorkflows = isCustomWorkflowsNavVisible(flags as Record<string, unknown>, {
    email: user?.email,
    mspId: userMspInfo?.msp_id,
  });
  const navigate = useNavigate();

  type LifecycleWorkflowAvailability =
    | { kind: "idle" }
    | { kind: "loading" }
    | { kind: "no_customer" }
    | { kind: "ready"; hasMatchingWorkflow: boolean };

  const [lifecycleWorkflowAvailability, setLifecycleWorkflowAvailability] =
    useState<LifecycleWorkflowAvailability>({ kind: "idle" });

  const [ticketLifecycleResolution, setTicketLifecycleResolution] = useState<{
    source: "pending" | "llm" | "fallback";
    value: UserLifecycle;
  }>({ source: "pending", value: "none" });

  useEffect(() => {
    setTicketLifecycleResolution({ source: "pending", value: "none" });
    setLifecycleWorkflowAvailability({ kind: "idle" });
  }, [ticketId, aiOverviewTicketContext]);

  const handleOverviewFetchResult = useCallback(
    (r: OverviewFetchResult) => {
      if (r.ok) {
        const raw = r.payload.user_lifecycle;
        const v = String(raw ?? "").trim().toLowerCase();
        if (v === "onboarding" || v === "offboarding" || v === "none") {
          setTicketLifecycleResolution({ source: "llm", value: v as UserLifecycle });
          return;
        }
      }
      const fb = userLifecycleFromTicketRecordHeuristic(ticketPayloadForLifecycle ?? null);
      setTicketLifecycleResolution({ source: "fallback", value: fb });
    },
    [ticketPayloadForLifecycle]
  );

  const lifecycleBannerLifecycle: "onboarding" | "offboarding" | null =
    canUseCustomWorkflows &&
    ticketLifecycleResolution.source !== "pending" &&
    (ticketLifecycleResolution.value === "onboarding" ||
      ticketLifecycleResolution.value === "offboarding")
      ? ticketLifecycleResolution.value
      : null;

  useEffect(() => {
    if (!lifecycleBannerLifecycle) {
      setLifecycleWorkflowAvailability({ kind: "idle" });
      return;
    }
    const cidRaw = customerId != null ? String(customerId).trim() : "";
    if (!cidRaw || Number.isNaN(Number(cidRaw))) {
      setLifecycleWorkflowAvailability({ kind: "no_customer" });
      return;
    }
    let cancelled = false;
    setLifecycleWorkflowAvailability({ kind: "loading" });
    customWorkflowApi
      .getWorkflows(cidRaw)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        const has = list.some((w: { workflow_type?: string | null }) =>
          customWorkflowMatchesTicketLifecycle(w.workflow_type, lifecycleBannerLifecycle)
        );
        setLifecycleWorkflowAvailability({ kind: "ready", hasMatchingWorkflow: has });
      })
      .catch(() => {
        if (!cancelled) {
          setLifecycleWorkflowAvailability({ kind: "ready", hasMatchingWorkflow: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [lifecycleBannerLifecycle, customerId, customWorkflowApi]);

  const navigateCustomWorkflows = useCallback(() => {
    const cidRaw = customerId != null ? String(customerId).trim() : "";
    const search =
      cidRaw && !Number.isNaN(Number(cidRaw))
        ? `?customer=${encodeURIComponent(cidRaw)}`
        : "";
    navigate({ pathname: "/custom_workflows", search });
  }, [navigate, customerId]);

  const handleOpenWorkflowDialog = useCallback(
    async (options?: { lifecycle?: "onboarding" | "offboarding" }) => {
      if (!canUseCustomWorkflows) {
        return;
      }
      const lifecycleFilter = options?.lifecycle ?? null;
      setWorkflowDialogLifecycleFilter(lifecycleFilter);
      setShowWorkflowDialog(true);
      setWorkflowsLoading(true);
      try {
        const cidRaw = customerId != null ? String(customerId).trim() : "";
        const customerIdForFetch =
          cidRaw && !Number.isNaN(Number(cidRaw)) ? cidRaw : undefined;

        let templateWorkflowsData: Workflow[] = [];
        let customWorkflowsData: unknown[] = [];

        if (lifecycleFilter) {
          if (!customerIdForFetch) {
            setWorkflows([]);
            return;
          }
          customWorkflowsData = await customWorkflowApi.getWorkflows(customerIdForFetch);
        } else {
          const customerIdNum = customerId ? Number(customerId) : null;
          [templateWorkflowsData, customWorkflowsData] = await Promise.all([
            WorkflowApi.getWorkflows(customerIdNum, ticketId),
            customWorkflowApi.getWorkflows(customerIdForFetch),
          ]);
        }

        const CUSTOM_WORKFLOW_ID_OFFSET = 1000000;
        const mappedCustomWorkflows: Workflow[] = (Array.isArray(customWorkflowsData)
          ? customWorkflowsData
          : []
        ).map((cw: any) => ({
          id: CUSTOM_WORKFLOW_ID_OFFSET + Number(cw.id),
          msp_id: Number(cw.msp_id),
          workflow_name: String(cw.workflow_name || ""),
          args_json: cw.steps_json,
          workflow_type: cw.workflow_type ? String(cw.workflow_type) : undefined,
          created_by: cw.created_by,
          created_at: cw.created_at,
          updated_at: cw.updated_at,
          guides: [],
          is_custom: true,
        }));

        let merged = lifecycleFilter
          ? mappedCustomWorkflows
          : [...(templateWorkflowsData || []), ...mappedCustomWorkflows];

        if (lifecycleFilter) {
          merged = merged.filter((w) =>
            customWorkflowMatchesTicketLifecycle(w.workflow_type, lifecycleFilter)
          );
        }

        const seen = new Set<string>();
        const deduped = merged.filter((w) => {
          const key = `${w.is_custom ? "custom" : "template"}::${w.workflow_name}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });

        setWorkflows(deduped);
      } catch (error) {
        console.error("Error fetching workflows:", error);
      } finally {
        setWorkflowsLoading(false);
      }
    },
    [canUseCustomWorkflows, customerId, ticketId, customWorkflowApi]
  );

  const relevantWorkflowDialogOptions = useMemo<
    { lifecycle: "onboarding" | "offboarding" } | undefined
  >(
    () =>
      lifecycleWorkflowAvailability.kind === "ready" &&
      lifecycleWorkflowAvailability.hasMatchingWorkflow &&
      lifecycleBannerLifecycle
        ? { lifecycle: lifecycleBannerLifecycle }
        : undefined,
    [lifecycleWorkflowAvailability, lifecycleBannerLifecycle]
  );

  const handleLifecycleCustomWorkflowsClick = useCallback(() => {
    if (relevantWorkflowDialogOptions) {
      void handleOpenWorkflowDialog(relevantWorkflowDialogOptions);
      return;
    }
    navigateCustomWorkflows();
  }, [
    relevantWorkflowDialogOptions,
    handleOpenWorkflowDialog,
    navigateCustomWorkflows,
  ]);

  const customWorkflowLifecycleCallout =
    lifecycleBannerLifecycle ? (
      <Box
        sx={{
          mb: 1.5,
          mx: isEmbedded ? 0.5 : 0,
          p: 1.5,
          borderRadius: 2,
          border: "1px solid #E8E0FF",
          backgroundColor: "rgba(155, 122, 239, 0.08)",
        }}
      >
        {lifecycleWorkflowAvailability.kind === "loading" ? (
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: 14,
              color: "#19213D",
              lineHeight: 1.5,
            }}
          >
            Checking for saved {lifecycleToCustomWorkflowType(lifecycleBannerLifecycle)} workflows for
            this customer…
          </Typography>
        ) : (
          <>
            <Typography
              sx={{
                fontFamily: "var(--theo-font-sans)",
                fontSize: 14,
                color: "#19213D",
                lineHeight: 1.5,
                mb: 1,
              }}
            >
              {lifecycleWorkflowAvailability.kind === "no_customer" ? (
                <>
                  This ticket looks like{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {lifecycleBannerLifecycle === "offboarding"
                      ? "user offboarding"
                      : "user onboarding"}
                  </Box>
                  . Open{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={navigateCustomWorkflows}
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      fontWeight: 600,
                      color: "#9B7AEF",
                      verticalAlign: "baseline",
                    }}
                  >
                    Custom Workflows
                  </MuiLink>{" "}
                  to create a saved workflow. Customer-specific lists appear when this ticket is linked to
                  a Horizon customer.
                </>
              ) : lifecycleWorkflowAvailability.kind === "ready" &&
                lifecycleWorkflowAvailability.hasMatchingWorkflow ? (
                <>
                  This ticket looks like{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {lifecycleBannerLifecycle === "offboarding"
                      ? "user offboarding"
                      : "user onboarding"}
                  </Box>
                  . Use{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={handleLifecycleCustomWorkflowsClick}
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      fontWeight: 600,
                      color: "#9B7AEF",
                      verticalAlign: "baseline",
                    }}
                  >
                    Custom Workflows
                  </MuiLink>{" "}
                  to run your saved M365 steps for this customer.
                </>
              ) : lifecycleWorkflowAvailability.kind === "ready" &&
                !lifecycleWorkflowAvailability.hasMatchingWorkflow ? (
                <>
                  This ticket looks like{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {lifecycleBannerLifecycle === "offboarding"
                      ? "user offboarding"
                      : "user onboarding"}
                  </Box>
                  . You don&apos;t have a saved{" "}
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    {lifecycleToCustomWorkflowType(lifecycleBannerLifecycle)}
                  </Box>{" "}
                  workflow for this customer yet. Create one in{" "}
                  <MuiLink
                    component="button"
                    type="button"
                    onClick={navigateCustomWorkflows}
                    sx={{
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      fontWeight: 600,
                      color: "#9B7AEF",
                      verticalAlign: "baseline",
                    }}
                  >
                    Custom Workflows
                  </MuiLink>{" "}
                  to run M365 steps from chat.
                </>
              ) : null}
            </Typography>
            {(lifecycleWorkflowAvailability.kind === "no_customer" ||
              lifecycleWorkflowAvailability.kind === "ready") && (
              <NeoButton
                variant="neo"
                size="small"
                onClick={handleLifecycleCustomWorkflowsClick}
                sx={{ mt: 0.5 }}
              >
                {lifecycleWorkflowAvailability.kind === "ready" &&
                lifecycleWorkflowAvailability.hasMatchingWorkflow
                  ? "Select Workflow"
                  : lifecycleWorkflowAvailability.kind === "ready" &&
                      !lifecycleWorkflowAvailability.hasMatchingWorkflow
                    ? "Create a Custom Workflow"
                    : "Go to Custom Workflows"}
              </NeoButton>
            )}
          </>
        )}
      </Box>
    ) : null;
  const {
    messages: chatMessages,
    isLoading: chatIsLoading,
    aiThreadId: reduxAiThreadId,
    sendMessage,
    stopTurn,
    queuedMessages,
    deleteQueuedMessage,
    updateQueuedMessage,
    sendQueuedMessageNow,
    canSendQueuedMessagesNow,
    connectionStatus,
  } = useChatHook(ticketId, userId, {
    aiThreadIdOverride: propAiThreadId ?? null,
    skipInitialize: skipAutoInitialize,
    onThreadName,
  });

  const messagesFromStore = useAppSelector((state: any) => state.chat.messages);
  const loadedTicketId = useAppSelector((state: any) => state.chat.ticketId);
  const chatLoadError = useAppSelector((state: any) => state.chat.error);
  const ticketConversationReady = isTicketConversationReady({
    ticketId,
    loadedTicketId,
    skipInitialize: skipAutoInitialize,
    loadError: chatLoadError,
  });

  const { isThinking, thinkingSteps, currentThinkingStep } = useSelector(
    (state: any) => state.chat
  );
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [screenshotLightboxSrc, setScreenshotLightboxSrc] = useState<string | null>(null);
  const lastAppliedPrefillRef = useRef<string>("");
  const [ticketSchedules, setTicketSchedules] = useState<TheoSchedule[]>([]);
  const [ticketSchedulesLoading, setTicketSchedulesLoading] = useState(false);
  const [ticketSchedulesAnchorEl, setTicketSchedulesAnchorEl] = useState<HTMLElement | null>(null);
  const ticketScheduleTimeZone = React.useMemo(() => getDefaultTimeZone(), []);

  const aiThreadId = propAiThreadId || reduxAiThreadId;
  const ticketResolverState = useTicketResolverState(aiThreadId, ticketId);
  const rawMessages =
    aiThreadId && Array.isArray(messagesFromStore) && messagesFromStore.length > 0
      ? messagesFromStore
      : (initialMessages || chatMessages);
  const messages = React.useMemo(
    () => normalizeMessages(Array.isArray(rawMessages) ? rawMessages : []),
    [rawMessages]
  );

  const lastMessageForScroll =
    messages.length > 0 ? messages[messages.length - 1] : undefined;
  const lastMessageId = lastMessageForScroll?.message_id;
  const lastContentType = lastMessageForScroll?.content_type;
  const lastContentLength = (lastMessageForScroll?.content ?? "").length;
  const scheduledHitlCount = React.useMemo(
    () => messages.filter((message) => message?.human_review_status === "scheduled").length,
    [messages]
  );
  const pendingWorkflowSchedule = React.useMemo(
    () => ticketSchedules.find(isPendingStructuredWorkflowResumeSchedule) || null,
    [ticketSchedules]
  );
  const ticketSchedulesNewestFirst = React.useMemo(
    () => sortSchedulesNewestFirst(ticketSchedules),
    [ticketSchedules]
  );
  const workflowChatDisabledReason = pendingWorkflowSchedule
    ? "Chat is disabled until workflow resumes."
    : null;
  const computerUseChatDisabledReason = useComputerUseChatDisabledReason(aiThreadId);
  const threadWait = useThreadWait(aiThreadId);
  // Theo's open question takes a chat reply: the composer stays open and says so.
  const chatReplyPlaceholder = useChatReplyPlaceholder(aiThreadId);

  /**
   * The messages in the order they are drawn, hoisted out of the render below.
   *
   * Browser runs are placed by position among these, so the panels have to be built from the
   * very same list the transcript renders -- deriving it twice would let the two drift and
   * put a panel in the wrong gap.
   */
  const displayedMessages = useMemo(() => {
    const collapsed = groupConsecutivePlanSteps(collapseStreamingChunks(messages as any[])) as unknown as Message[];
    return [...collapsed].sort(compareMessages);
  }, [messages]);

  const renderComputerUseRuns = useComputerUseRuns(aiThreadId, displayedMessages);

  const liveAutomationCapacity = useAppSelector((state: any) => state.chat.threadAutomationByThreadId?.[String(aiThreadId)]);
  const threadAutomationCapacity = useThreadAutomationBadge(aiThreadId, displayedMessages.length > 0, liveAutomationCapacity);
  // The selector offers each enabled agent; picking one sends its ID and the server runs that
  // agent's config. The thread's own agent is read so a poller-started specialist shows as itself.
  const { agents: mspAgents } = useAgents(showTicketChatModeSelector ? userMspInfo?.msp_id : undefined);
  const modeAgents = useMemo(() => selectableAgents(mspAgents), [mspAgents]);
  const agentNameForId = useCallback(
    (id: number | null | undefined) => (id ? mspAgents.find((agent) => agent.id === id)?.name ?? null : null),
    [mspAgents]
  );
  const threadAgentId = useThreadAgentId(showTicketChatModeSelector ? aiThreadId : null, liveAutomationCapacity ?? null);
  const [ticketChatMode, setTicketChatMode, modeSendOptions] = useTicketChatMode(
    aiThreadId,
    liveAutomationCapacity ?? null,
    threadAgentId
  );
  const modeLoading = showTicketChatModeSelector && ticketChatMode === null;
  const interruptAutoRun = showTicketChatModeSelector && modeSendOptions?.interruptAutoRun === true;
  const chatDisabledReason = workflowChatDisabledReason || (modeLoading ? "Loading conversation mode…" : null) ||
    (interruptAutoRun ? null : composeChatDisabledReason(threadWait, computerUseChatDisabledReason));

  const threadAutomationBadgeId = useMemo(
    () =>
      threadAutomationCapacity
        ? threadAutomationBadgeMessageId(displayedMessages, (message) => isNeoAssistantAuthor(message.user_id))
        : null,
    [displayedMessages, threadAutomationCapacity]
  );

  const loadTicketSchedules = useCallback(async () => {
    if (!schedulerEnabled || !ticketId) {
      setTicketSchedules([]);
      return;
    }
    setTicketSchedulesLoading(true);
    try {
      const response = await scheduleApi.listSchedules({
        ticket_id: ticketId,
        ai_thread_id: aiThreadId || null,
        status: "pending",
        limit: 50,
      });
      setTicketSchedules(Array.isArray(response.schedules) ? response.schedules : []);
    } catch (error) {
      setTicketSchedules([]);
    } finally {
      setTicketSchedulesLoading(false);
    }
  }, [aiThreadId, scheduleApi, schedulerEnabled, ticketId]);

  useEffect(() => {
    void loadTicketSchedules();
  }, [loadTicketSchedules, lastMessageId, scheduledHitlCount]);

  useEffect(() => {
    if (!pendingWorkflowSchedule) return;
    const timer = window.setInterval(() => {
      void loadTicketSchedules();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [loadTicketSchedules, pendingWorkflowSchedule]);

  const openTicketSchedulesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setTicketSchedulesAnchorEl(event.currentTarget);
    void loadTicketSchedules();
  };

  const closeTicketSchedulesMenu = () => {
    setTicketSchedulesAnchorEl(null);
  };

  /** Ticket-bar Schedules dropdown: open this row on the Schedules page. */
  const openTicketSchedule = (schedule: TheoSchedule) => {
    closeTicketSchedulesMenu();
    if (!schedule.id) return;
    const href = `/schedules?scheduleId=${encodeURIComponent(String(schedule.id))}`;
    const opened = window.open(href, "_blank", "noopener,noreferrer");
    if (opened) {
      opened.opener = null;
    }
  };

  const refreshTicketThreadAfterWorkflowResume = async () => {
    if (!ticketId) {
      dispatch({ type: ActionTypes.END_THINKING });
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
      return;
    }
    try {
      const response: any = await chatApiInstance.getTicketThread(ticketId);
      dispatch({
        type: ActionTypes.GET_TICKET_THREAD,
        payload: {
          ...response,
          ai_thread: {
            ...response?.ai_thread,
            ticket_id: response?.ai_thread?.ticket_id || { value: ticketId },
          },
        },
      });
    } catch {
      dispatch({ type: ActionTypes.END_THINKING });
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
    }
  };

  const handleCancelTicketSchedule = async (schedule: TheoSchedule) => {
    if (!schedule.id) return;
    const shouldShowResumeProgress = Boolean(isPendingStructuredWorkflowResumeSchedule(schedule));
    setCancelingScheduleId(String(schedule.id));
    if (shouldShowResumeProgress) {
      dispatch({ type: ActionTypes.CHAT_LOADING, payload: true });
      dispatch({ type: ActionTypes.START_THINKING });
    }
    try {
      await scheduleApi.cancelSchedule(schedule.id);
      await loadTicketSchedules();
      if (shouldShowResumeProgress) {
        await refreshTicketThreadAfterWorkflowResume();
      }
    } catch (error) {
      if (shouldShowResumeProgress) {
        dispatch({ type: ActionTypes.END_THINKING });
        dispatch({ type: ActionTypes.CHAT_LOADING, payload: false });
      }
      setWorkflowScheduleSnackbar({
        open: true,
        message: "Failed to cancel schedule.",
        severity: "error",
      });
    } finally {
      setCancelingScheduleId(null);
    }
  };

  const openDeleteModal = async () => {
    let count = 0;
    if (schedulerEnabled && ticketId) {
      try {
        const response = await scheduleApi.listSchedules({
          ticket_id: ticketId,
          status: "pending",
          limit: 50,
        });
        count = Array.isArray(response.schedules) ? response.schedules.length : 0;
      } catch {
        count = ticketSchedules.length;
      }
    }
    setDeletePendingScheduleCount(count);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    chatApiInstance.preloadTicketAuditData(ticketId ?? null);
    chatApiInstance.preloadToolAuditLogs(aiThreadId ?? null);
  }, [ticketId, aiThreadId]);

  useEffect(() => {
    const root = messagesScrollRef.current;
    if (!root) return;
    const onClick = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const img = t.closest("img.neo-chat-inline-image") as HTMLImageElement | null;
      if (img?.src) {
        setScreenshotLightboxSrc(img.src);
        e.preventDefault();
        e.stopPropagation();
      }
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, [messages, chatEnabled]);
  
  // DISABLED: fetch pills
  // useEffect(() => {
  //   if (customerId) {
  //     dispatch(fetchPills(customerId));
  //   }
  // }, [dispatch, customerId]);

  // Check if chat is enabled for this ticket based on queue configuration
  useEffect(() => {
    if (!ticketId) {
      setChatEnabled(true);
      setCheckingChatEnabled(false);
      return;
    }

    let isActive = true;

    const checkChatEnabled = async () => {
      setCheckingChatEnabled(true);
      try {
        const response = await supervisorApi.getTicketChatEnabled(ticketId);
        const enabled = response?.enabled === true;
        if (isActive) {
          setChatEnabled(enabled);
        }
      } catch (error) {
        console.error('Error checking chat enabled status:', error);
        if (isActive) {
          setChatEnabled(true);
        }
      } finally {
        if (isActive) {
          setCheckingChatEnabled(false);
        }
      }
    };

    checkChatEnabled();

    return () => {
      isActive = false;
    };
  }, [ticketId, supervisorApi]);

  // Fetch productivity apps status for the customer
  useEffect(() => {
    // Missing customer details are not evidence of missing integrations.
    // Keep the warning hidden until the customer's status has been read.
    setHasProductivityApps(null);
    if (!customerId || !ticketId) {
      return;
    }
    let cancelled = false;
    integrationApiInstance
      .getCustomerProductivityAppsStatus(customerId)
      .then((response: any) => {
        if (!cancelled) {
          setHasProductivityApps(response.has_productivity_apps);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHasProductivityApps(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, ticketId]);

  useEffect(() => {
    const el = messagesEndRef.current;
    if (!el) return;
    const last = messages[messages.length - 1];
    const streaming = last?.user_id === "neo" && last?.isStreaming;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: streaming ? "auto" : "smooth",
        block: isEmbedded ? "nearest" : "end",
      });
    });
  }, [
    messages.length,
    lastMessageId,
    lastContentType,
    lastContentLength,
    isThinking,
    thinkingSteps.length,
    isEmbedded,
    messages,
  ]);

  useEffect(() => {
    const normalized = String(prefillMessage || "").trim();
    if (!normalized) return;
    if (normalized === lastAppliedPrefillRef.current) return;
    if (inputMessage.trim()) return;
    setInputMessage(normalized);
    lastAppliedPrefillRef.current = normalized;
  }, [prefillMessage, inputMessage]);

  // DISABLED: pills / prompts — re-enable with PillsBar + fetchPills + state above
  // const selectedSuggestions = selectedPill
  //   ? pills.find((p: any) => p.id === selectedPill)?.suggestions || []
  //   : [];
  // const handlePillClick = (pillId: string) => {
  //   const pill = pills.find((p: any) => p.id === pillId);
  //   if (pill) {
  //     setInputMessage(pill.label);
  //     setSelectedPill(pill.id);
  //     setShowPrompts(true);
  //     setTimeout(() => {
  //       if (inputRef.current) inputRef.current.blur();
  //     }, 0);
  //   }
  // };
  // const handleSuggestionClick = (text: string) => {
  //   setInputMessage(text);
  //   setShowPrompts(false);
  //   setTimeout(() => {
  //     if (inputRef.current) inputRef.current.focus();
  //   }, 0);
  // };
  // const handleClosePrompts = () => {
  //   setSelectedPill(null);
  //   setTimeout(() => {
  //     if (inputRef.current) inputRef.current.focus();
  //   }, 0);
  // };

  // Typeahead handlers
  const handleInputChange = (newValue: string) => {
    setInputMessage(newValue);
    
    if (typeaheadDebounceTimer) {
      clearTimeout(typeaheadDebounceTimer);
    }
    
    if (newValue.trim().length > 0) {
      // Adaptive debounce: faster for short queries, slower for longer ones
      // Short text (< 15 chars or < 3 words): 100ms for quick feedback
      // Longer text: 250ms to reduce API calls as user types longer queries
      const wordCount = newValue.trim().split(/\s+/).length;
      const charCount = newValue.trim().length;
      const debounceDelay = (charCount < 15 || wordCount < 3) ? 100 : 250;
      
      const timer = setTimeout(() => {
        setShowTypeahead(true);
        setDebouncedInputText(newValue); // Set debounced input for API calls
      }, debounceDelay);
      setTypeaheadDebounceTimer(timer);
    } else {
      setShowTypeahead(false);
      setDebouncedInputText(''); // Clear debounced input
    }
  };

  const handleTypeaheadSelect = (selectedText: string) => {
    setInputMessage(selectedText);
    setShowTypeahead(false);
    // Focus the input after selection
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleChoiceOptionClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement | null;
    const option = target?.closest?.("[data-neo-choice-option]") as HTMLElement | null;
    const choiceText = option?.getAttribute("data-neo-choice-option")?.trim();
    if (!choiceText) return;

    event.preventDefault();
    setInputMessage(choiceText);
    setShowTypeahead(false);
    setTimeout(() => {
      if (inputRef.current) inputRef.current.focus();
    }, 0);
  };

  const handleTypeaheadClose = () => {
    setShowTypeahead(false);
  };

  const handleTypeaheadNavigationChange = (hasNavigated: boolean) => {
    setHasNavigatedToTypeahead(hasNavigated);
  };

  const handleInputSend = (messageOverride?: string | ChatSendPayload) => {
    const payload =
      typeof messageOverride === "object" && messageOverride !== null
        ? messageOverride
        : { content: String(messageOverride ?? inputMessage) };
    const trimmedMessage = String(payload.content || "").trim();
    const attachments = Array.isArray(payload.attachments) ? payload.attachments : [];
    if (
      (!trimmedMessage && attachments.length === 0) ||
      chatEnabled !== true ||
      workflowChatDisabledReason || modeLoading
    ) return;

    const sendOpts = showTicketChatModeSelector
      ? modeSendOptions
      : undefined;
    sendMessage(trimmedMessage, { ...(sendOpts || {}), attachments });
    setInputMessage("");
    setShowTypeahead(false);
  };

  const nullStatePrompts = React.useMemo(
    () => (Array.isArray(nullStatePromptsProp) && nullStatePromptsProp.length ? nullStatePromptsProp : ["Please handle this ticket"]),
    [nullStatePromptsProp]
  );
  const handleNullStatePromptClick = (prompt: string) => {
    if ((chatIsLoading && !interruptAutoRun) || chatEnabled !== true || workflowChatDisabledReason || modeLoading) return;
    const sendOpts = showTicketChatModeSelector
      ? modeSendOptions
      : undefined;
    sendMessage(prompt, sendOpts);
  };

  const getDefaultFileLinearTaskTitle = useCallback(() => {
    const title = String(ticketTitle || "").trim();
    if (title) return title;
    return ticketDisplayId ? `Ticket ${ticketDisplayId}` : "Theo chat task";
  }, [ticketDisplayId, ticketTitle]);

  const getDefaultFileLinearTaskTicketLink = useCallback(() => {
    const link = String(ticketLink || "").trim();
    if (link) return link;
    if (typeof window !== "undefined") return window.location.href;
    return "";
  }, [ticketLink]);

  const handleOpenFileLinearTaskDialog = useCallback(() => {
    const fallbackAssignee = String(currentUser?.email || userId || "").trim();
    const fallbackPrefix = fallbackAssignee.split("@")[0]?.toLowerCase() || "";
    setFileLinearTaskTitle(getDefaultFileLinearTaskTitle());
    setFileLinearTaskDescription("");
    setFileLinearTaskTicketLink(getDefaultFileLinearTaskTicketLink());
    setFileLinearTaskTraceUrl("");
    setFileLinearTaskAssignee(fallbackAssignee);
    setFileLinearTaskAssigneesLoading(true);
    setFileLinearTaskTraceLoading(Boolean(aiThreadId));
    setShowFileLinearTaskDialog(true);
    if (aiThreadId) {
      chatApiInstance
        .getLinearTaskTraceUrl(String(aiThreadId))
        .then((result) => {
          const traceUrl = String(result?.trace_url || "").trim();
          if (traceUrl) {
            setFileLinearTaskTraceUrl(traceUrl);
          }
        })
        .catch(() => {
          setFileLinearTaskTraceUrl("");
        })
        .finally(() => setFileLinearTaskTraceLoading(false));
    }
    chatApiInstance
      .getLinearTaskAssignees()
      .then((result) => {
        const list = Array.isArray(result?.assignees) ? result.assignees : [];
        setFileLinearTaskAssignees(list);
        if (list.length > 0) {
          const exactAssignee = list.find(
            (assignee) => assignee.email.toLowerCase() === fallbackAssignee.toLowerCase()
          );
          const prefixAssignee = fallbackPrefix
            ? list.find((assignee) => assignee.email.split("@")[0]?.toLowerCase() === fallbackPrefix)
            : undefined;
          setFileLinearTaskAssignee((exactAssignee || prefixAssignee || list[0]).email);
        }
      })
      .catch((error: any) => {
        setFileLinearTaskAssignees([]);
        setFileLinearTaskSnackbar({
          open: true,
          severity: "error",
          message: error?.message || "Failed to load Linear assignees.",
        });
      })
      .finally(() => setFileLinearTaskAssigneesLoading(false));
  }, [aiThreadId, currentUser?.email, getDefaultFileLinearTaskTicketLink, getDefaultFileLinearTaskTitle, userId]);

  const handleSubmitFileLinearTask = useCallback(async () => {
    if (!aiThreadId || !fileLinearTaskTitle.trim()) return;
    setFileLinearTaskSubmitting(true);
    try {
      const result = await chatApiInstance.fileLinearTask(String(aiThreadId), {
        title: fileLinearTaskTitle.trim(),
        description: fileLinearTaskDescription.trim(),
        ticket_link: fileLinearTaskTicketLink.trim(),
        assignee_email: fileLinearTaskAssignee.trim(),
        trace_url: fileLinearTaskTraceUrl.trim(),
      });
      const identifier = result.identifier || result.issue_id || "Linear task";
      setCreatedLinearTask({
        identifier,
        title: result.title || fileLinearTaskTitle.trim(),
        url: result.url || "",
      });
      setShowFileLinearTaskDialog(false);
    } catch (error: any) {
      setFileLinearTaskSnackbar({
        open: true,
        severity: "error",
        message: error?.message || "Failed to create Linear task.",
      });
    } finally {
      setFileLinearTaskSubmitting(false);
    }
  }, [
    aiThreadId,
    fileLinearTaskAssignee,
    fileLinearTaskDescription,
    fileLinearTaskTicketLink,
    fileLinearTaskTitle,
    fileLinearTaskTraceUrl,
  ]);

  const getWorkflowStepLabel = (action: string): string => workflowActionDisplayLabel(action);

  const getWorkflowStepSummary = (workflow: Workflow): string | null => {
    if (!workflow?.args_json) return null;
    let parsed: any = workflow.args_json;
    try {
      parsed = typeof parsed === "string" ? JSON.parse(parsed || "{}") : parsed;
    } catch {
      return null;
    }

    const stepsCandidate =
      (parsed && Array.isArray(parsed?.steps) && parsed.steps) ||
      (parsed && Array.isArray(parsed?.steps_json) && parsed.steps_json) ||
      (parsed && Array.isArray(parsed?.steps_json?.steps) && parsed.steps_json.steps) ||
      (parsed && Array.isArray(parsed?.stepsJson?.steps) && parsed.stepsJson.steps) ||
      null;

    if (!Array.isArray(stepsCandidate) || stepsCandidate.length === 0) return null;

    const labels = stepsCandidate
      .map((s: any) => {
        const action = s?.action;
        if (!action) return null;
        const label = getWorkflowStepLabel(String(action));
        return label?.trim() ? label : null;
      })
      .filter(Boolean) as string[];

    if (labels.length === 0) return null;
    const maxSteps = 5;
    const shown = labels.slice(0, maxSteps);
    const moreCount = labels.length - shown.length;
    return `Steps: ${shown.join(", ")}${moreCount > 0 ? `, +${moreCount} more` : ""}`;
  };

  const workflowAutoContinueHitlEnabled = (workflow: Workflow): boolean => {
    if (!workflow?.is_custom || !workflow.args_json) return false;
    try {
      const parsed =
        typeof workflow.args_json === "string"
          ? JSON.parse(workflow.args_json || "{}")
          : workflow.args_json;
      return parsed?.auto_continue_hitl === true;
    } catch {
      return false;
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (typeaheadDebounceTimer) {
        clearTimeout(typeaheadDebounceTimer);
      }
    };
  }, [typeaheadDebounceTimer]);

  // const filteredPills = pillsKeyword
  //   ? pills.filter((pill: any) =>
  //       pill.label.toLowerCase().includes(pillsKeyword.toLowerCase())
  //     )
  //   : pills;

  // Handle workflow selection. Optional delay overrides come from Configure → Play now.
  const handleSelectWorkflow = (
    workflow: Workflow,
    stepDelayOverrides?: Array<{
      index: number;
      id?: string;
      action?: string;
      schedule_delay_enabled: string;
      schedule_delay_amount?: string;
      schedule_delay_unit?: string;
      schedule_delay_reason?: string;
    }>
  ) => {
    let workflowPrompt: string;
    let structuredWorkflowSteps:
      | { action: string; label: string; id: string; options?: Record<string, string> }[]
      | undefined;
    let structuredWorkflowAutoContinueHitl = false;

    if (workflow.is_custom && workflow.args_json) {
      // Custom workflow: pass steps as actions for the LLM to execute, with ticket as input source
      let stepsJson: { steps?: any[]; auto_continue_hitl?: boolean };
      try {
        stepsJson = typeof workflow.args_json === "string"
          ? JSON.parse(workflow.args_json || "{}")
          : workflow.args_json;
      } catch {
        stepsJson = {};
      }
      const steps = stepsJson?.steps || [];
      structuredWorkflowAutoContinueHitl = stepsJson?.auto_continue_hitl === true;
      const filteredSteps = steps.filter((s: any) => s?.action);
      structuredWorkflowSteps = filteredSteps.map((s: any) => {
        const action = String(s.action);
        const base = {
          action,
          label: stepTitleForTechnicianSummary({ action, label: s.label }),
          id: String(s.id || ""),
        };
        if (s.options && typeof s.options === "object" && !Array.isArray(s.options)) {
          return { ...base, options: { ...s.options } as Record<string, string> };
        }
        return base;
      });
      if (stepDelayOverrides && structuredWorkflowSteps) {
        structuredWorkflowSteps = applyWorkflowStepDelayOverridesToSteps(
          structuredWorkflowSteps,
          stepDelayOverrides
        );
      }
      if (filteredSteps.length > 0) {
        const wt = String((workflow as Workflow & { workflow_type?: string }).workflow_type || "").trim();
        workflowPrompt = buildTechnicianWorkflowChatSummary({
          workflowName: workflow.workflow_name,
          workflowType: wt || undefined,
          steps: structuredWorkflowSteps || filteredSteps,
        });
      } else {
        workflowPrompt = `run: ${workflow.workflow_name}`;
      }
    } else {
      workflowPrompt = `run: ${workflow.workflow_name}`;
    }

    setInputMessage(workflowPrompt);
    setShowWorkflowDialog(false);
    // Automatically send the workflow prompt
    setTimeout(() => {
      if ((!chatIsLoading || interruptAutoRun) && !workflowChatDisabledReason && !modeLoading) {
        const swPayload =
          structuredWorkflowSteps && structuredWorkflowSteps.length > 0
            ? {
                workflow_name: workflow.workflow_name,
                steps: structuredWorkflowSteps,
                suppress_playbooks: true,
                auto_continue_hitl: structuredWorkflowAutoContinueHitl,
              }
            : undefined;
        const baseOpts = swPayload ? { structuredWorkflow: swPayload } : {};
        const autoOpts = showTicketChatModeSelector
          ? modeSendOptions
          : undefined;
        sendMessage(workflowPrompt, { ...baseOpts, ...autoOpts });
        setInputMessage("");
      }
    }, 100);
  };

  const handleOpenWorkflowSchedule = (event: React.MouseEvent, workflow: Workflow) => {
    event.stopPropagation();
    if (workflowChatDisabledReason) return;
    setWorkflowScheduleError(null);
    setWorkflowToSchedule(workflow);
  };

  const handleConfirmWorkflowSchedule = async ({
    schedule,
    scheduledFor,
    timezone,
    stepDelayOverrides,
  }: {
    schedule: boolean;
    scheduledFor?: string;
    timezone: string;
    stepDelayOverrides: Array<{
      index: number;
      id?: string;
      action?: string;
      schedule_delay_enabled: string;
      schedule_delay_amount?: string;
      schedule_delay_unit?: string;
      schedule_delay_reason?: string;
    }>;
  }) => {
    if (!workflowToSchedule) {
      setWorkflowScheduleError("Select a workflow first.");
      return;
    }
    if (!schedule) {
      const workflow = workflowToSchedule;
      setWorkflowToSchedule(null);
      setWorkflowScheduleError(null);
      setShowWorkflowDialog(false);
      setWorkflowDialogLifecycleFilter(null);
      handleSelectWorkflow(workflow, stepDelayOverrides);
      return;
    }
    if (!ticketId) {
      setWorkflowScheduleError("A ticket is required to schedule this workflow.");
      return;
    }
    if (!scheduledFor) {
      setWorkflowScheduleError("Run at is required when Schedule is checked.");
      return;
    }
    setWorkflowScheduleSubmitting(true);
    setWorkflowScheduleError(null);
    try {
      await scheduleApi.scheduleWorkflowStart({
        workflow_id: workflowToSchedule.id,
        ticket_id: ticketId,
        ticket_title: ticketTitle || ticketDisplayId || "",
        ai_thread_id: aiThreadId || null,
        scheduled_for: scheduledFor,
        timezone,
        step_delay_overrides: stepDelayOverrides,
      });
      setWorkflowToSchedule(null);
      setShowWorkflowDialog(false);
      setWorkflowDialogLifecycleFilter(null);
      setWorkflowScheduleSnackbar({
        open: true,
        message: `${workflowToSchedule.workflow_name} is scheduled.`,
        severity: "success",
      });
      await loadTicketSchedules();
    } catch (error: any) {
      setWorkflowScheduleError(error?.message || "Failed to schedule workflow");
    } finally {
      setWorkflowScheduleSubmitting(false);
    }
  };

  const fileLinearTaskAssigneeOptions = React.useMemo(() => {
    const options = [...fileLinearTaskAssignees];
    const fallbackEmail = String(currentUser?.email || userId || "").trim();
    if (fallbackEmail && !options.some((assignee) => assignee.email.toLowerCase() === fallbackEmail.toLowerCase())) {
      options.unshift({ id: "current-user", name: fallbackEmail, email: fallbackEmail });
    }
    return options;
  }, [currentUser?.email, fileLinearTaskAssignees, userId]);

  const fileLinearTaskMspLabel = React.useMemo(() => {
    const mspId = userMspInfo?.msp_id ? String(userMspInfo.msp_id) : "";
    const mspName = String(userMspInfo?.msp_name || "").trim();
    if (mspId && mspName) return `${mspName} (MSP ID: ${mspId})`;
    if (mspName) return mspName;
    if (mspId) return `MSP ID: ${mspId}`;
    return "";
  }, [userMspInfo?.msp_id, userMspInfo?.msp_name]);

  return (
    <Paper
      sx={{
        p: isEmbedded ? 0 : 2,
        display: "flex",
        flexDirection: "column",
        border: isEmbedded ? "none" : "1px solid #E2E8F0",
        borderRadius: isEmbedded ? 0 : "12px",
        boxShadow: isEmbedded ? "none" : "0 2px 8px rgba(15, 23, 42, 0.04)",
        height: useParentHeight
          ? "100%"
          : "calc(100vh - 64px - 24px - 24px)",
        maxHeight: useParentHeight
          ? "100%"
          : "calc(100vh - 64px - 24px - 24px)",
        minHeight: 0,
        background: isEmbedded ? "transparent" : "#FFFFFF",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {/* Header */}
      {!hideHeader && (
        <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: isEmbedded ? 0.5 : 1.25,
          py: isEmbedded ? 0.5 : 0,
          px: isEmbedded ? 1.5 : 0,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", paddingLeft: isEmbedded ? 0 : 0.5 }}>
          <img
            src={theoLogo}
            alt="Neo Link"
            style={{ 
              width: isEmbedded ? 45 : 64,
              height: isEmbedded ? 30 : 36,
              objectFit: "contain",
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isEmbedded ? 0.75 : 1,
            minWidth: 0,
          }}
        >
          <TicketResolverStateChip
            resolverState={ticketResolverState?.resolver_state}
            resolverContext={ticketResolverState?.resolver_context}
          />
          {chatEnabled === true && (
            <ConnectionNotice status={connectionStatus} compact={Boolean(isEmbedded)} />
          )}
          {/* Grouped toolbar: one bordered segment, one quiet color; Clear chat turns red only on hover. */}
          <Box role="toolbar" aria-label="Ticket chat actions" sx={TOOLBAR_GROUP_SX}>
          {/* LangSmith: ROOT-only, first segment (ticket + device chat). */}
          {canOpenLangSmith ? (
            <LangSmithThreadLinkButton
              aiThreadId={aiThreadId}
              variant="toolbar"
              compact={Boolean(isEmbedded)}
            />
          ) : null}
          {/* Ticket-bar Schedules dropdown (clock): pending rows for this thread. Click a row to open the schedule; X cancels. */}
          {schedulerEnabled && ticketId && ticketSchedules.length > 0 && (
            <>
              <Tooltip title={`${ticketSchedules.length} pending schedule${ticketSchedules.length === 1 ? "" : "s"}`}>
                <span>
                  <IconButton
                    size="small"
                    onClick={openTicketSchedulesMenu}
                    aria-label="Schedules"
                    aria-controls={ticketSchedulesAnchorEl ? "ticket-schedules-menu" : undefined}
                    aria-haspopup="menu"
                    aria-expanded={ticketSchedulesAnchorEl ? "true" : undefined}
                    sx={toolbarButtonSx(Boolean(isEmbedded))}
                  >
                    <Badge
                      badgeContent={ticketSchedules.length}
                      max={99}
                      sx={{
                        "& .MuiBadge-badge": {
                          backgroundColor: VIOLET.main,
                          color: "#FFFFFF",
                          minWidth: 14,
                          height: 14,
                          fontSize: 9,
                          fontWeight: 700,
                          px: 0.35,
                        },
                      }}
                    >
                      <ScheduleOutlinedIcon sx={{ fontSize: toolbarIconSize(Boolean(isEmbedded)) }} />
                    </Badge>
                  </IconButton>
                </span>
              </Tooltip>
              <Menu
                id="ticket-schedules-menu"
                anchorEl={ticketSchedulesAnchorEl}
                open={Boolean(ticketSchedulesAnchorEl)}
                onClose={closeTicketSchedulesMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 0.75,
                    width: 340,
                    maxWidth: "calc(100vw - 32px)",
                    borderRadius: 1,
                    border: "1px solid #E5E7EB",
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
                    overflow: "hidden",
                  },
                }}
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderBottom: "1px solid #EEF2F7",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography sx={{ fontFamily: "Inter", fontWeight: 700, fontSize: 13, color: "#111827" }}>
                    Schedules
                  </Typography>
                  {ticketSchedulesLoading ? (
                    <CircularProgress size={14} thickness={5} />
                  ) : (
                    <Typography sx={{ fontFamily: "Inter", fontSize: 12, color: "#6B7280" }}>
                      {ticketSchedules.length}
                    </Typography>
                  )}
                </Box>
                {ticketSchedulesNewestFirst.map((schedule) => (
                  <MenuItem
                    key={String(schedule.id)}
                    aria-label={`Open schedule ${scheduleDropdownTitle(schedule)}`}
                    onClick={() => openTicketSchedule(schedule)}
                    sx={{
                      alignItems: "flex-start",
                      gap: 1.25,
                      px: 1.5,
                      py: 1.1,
                      borderBottom: "1px solid #F3F4F6",
                      whiteSpace: "normal",
                      "&:last-of-type": { borderBottom: 0 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: scheduleStatusDotColor(schedule.status),
                        mt: 0.75,
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 700,
                          fontSize: 13,
                          color: "#111827",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {scheduleDropdownTitle(schedule)}
                      </Typography>
                      <Typography sx={{ fontFamily: "Inter", fontSize: 12, color: "#6B7280", mt: 0.25 }}>
                        {formatDateTimeInTimeZone(schedule.scheduled_for, ticketScheduleTimeZone)}
                      </Typography>
                      <Typography sx={{ fontFamily: "Inter", fontSize: 11, color: "#6B7280", mt: 0.25 }}>
                        {scheduleDropdownStatusText(schedule)}
                      </Typography>
                    </Box>
                    <Tooltip title="Cancel schedule">
                      <span>
                        <IconButton
                          size="small"
                          aria-label="Cancel schedule"
                          disabled={cancelingScheduleId === String(schedule.id)}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void handleCancelTicketSchedule(schedule);
                          }}
                          sx={{ mt: 0.1, color: "#6B7280" }}
                        >
                          {cancelingScheduleId === String(schedule.id) ? (
                            <CircularProgress size={14} thickness={5} />
                          ) : (
                            <CloseIcon sx={{ fontSize: 16 }} />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          {chatEnabled === true && canUseCustomWorkflows && (
            <Tooltip title={workflowChatDisabledReason || "Start workflow"}>
              <span>
                <IconButton
                  size="small"
                  aria-label="Start workflow"
                  onClick={() => {
                    if (!workflowChatDisabledReason) {
                      void handleOpenWorkflowDialog(relevantWorkflowDialogOptions);
                    }
                  }}
                  disabled={Boolean(workflowChatDisabledReason)}
                  sx={toolbarButtonSx(Boolean(isEmbedded))}
                >
                  <AccountTreeOutlinedIcon sx={{ fontSize: toolbarIconSize(Boolean(isEmbedded)) }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {chatEnabled === true && ticketId && canFileLinearTask && (
            <Tooltip title="File Linear task">
              <span>
                <IconButton
                  size="small"
                  aria-label="File Linear task"
                  onClick={handleOpenFileLinearTaskDialog}
                  disabled={!aiThreadId}
                  sx={toolbarButtonSx(Boolean(isEmbedded))}
                >
                  <AddTaskOutlinedIcon sx={{ fontSize: toolbarIconSize(Boolean(isEmbedded)) }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {chatEnabled === true && (
            <Tooltip title="Audit logs">
              <span>
                <IconButton
                  size="small"
                  aria-label="Audit logs"
                  onClick={() => setAuditLogPopupOpen(true)}
                  disabled={!aiThreadId}
                  sx={toolbarButtonSx(Boolean(isEmbedded))}
                >
                  <ShieldOutlinedIcon sx={{ fontSize: toolbarIconSize(Boolean(isEmbedded)) }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {chatEnabled === true && ticketId ? (
            <ClearChatButton
              compact={Boolean(isEmbedded)}
              onClearChat={() => {
                void openDeleteModal();
              }}
            />
          ) : null}
          </Box>
          {headerRightAction}
          <IconButton
            size="small"
            aria-label="Collapse"
            sx={toolbarStandaloneButtonSx(Boolean(isEmbedded))}
          >
            <ChevronRightIcon sx={{ fontSize: toolbarIconSize(Boolean(isEmbedded)) + 3 }} />
          </IconButton>
        </Box>
      </Box>
      )}
      {!hideHeader && (
        <Divider sx={{ mb: isEmbedded ? 1 : 2, flexShrink: 0, borderColor: NEUTRAL.border }} />
      )}

      {/* Check if chat is enabled - show disabled message if not, or loading state while checking */}
      {chatEnabled === false && (
        <Box
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: isEmbedded ? 1.5 : 2,
            py: 4,
            overflow: "auto",
          }}
        >
          <Typography
            sx={{
              fontFamily: "var(--theo-font-sans)",
              fontSize: `${fontSizes.body}px`,
              color: "#666F8D",
              textAlign: "center",
            }}
          >
            Theo is not enabled for this ticket.
          </Typography>
        </Box>
      )}
      
      {/* Show loading state while checking */}
      {chatEnabled === null && checkingChatEnabled && (
        <Box
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: isEmbedded ? 1.5 : 2,
            py: 4,
            overflow: "auto",
          }}
        >
          <TypingIndicator />
        </Box>
      )}

      {/* Messages - only show if chat is enabled (true) */}
      {chatEnabled === true && (
        <Box
          ref={messagesScrollRef}
          sx={{
            flex: "1 1 0%",
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
            ...(isEmbedded ? { px: 1.5 } : { px: 0.5, pr: 1 }),
            fontFamily: "var(--theo-font-sans)",
            mb: 2,
            fontSize: `${fontSizes.body}px`,
          }}
        >
        {!ticketConversationReady ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 320,
              py: 4,
            }}
          >
            <TypingIndicator />
          </Box>
        ) : (
        <>
        {(() => {
          const sortedMessages = displayedMessages;

          const hideNullStatePrompts =
            chatIsLoading ||
            sortedMessages.some((m) => Boolean((m as Message).isStreaming));

          const nullState = (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 320,
                py: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.25,
                  alignItems: "center",
                }}
              >
                {nullStatePrompts.map((promptText) => (
                  <Box
                    key={promptText}
                    component="button"
                    type="button"
                    onClick={() => handleNullStatePromptClick(promptText)}
                    disabled={chatIsLoading || chatEnabled !== true || Boolean(workflowChatDisabledReason)}
                    sx={{
                      border: "1px solid #E1E5EC",
                      borderRadius: "6px",
                      px: 2.5,
                      py: 1.25,
                      background: "#FFFFFF",
                      color: "#526071",
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: "14px",
                      fontWeight: 500,
                      cursor:
                        chatIsLoading || chatEnabled !== true || workflowChatDisabledReason
                          ? "not-allowed"
                          : "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background:
                          chatIsLoading || chatEnabled !== true || workflowChatDisabledReason
                            ? "#FFFFFF"
                            : "#F7F6FF",
                        borderColor:
                          chatIsLoading || chatEnabled !== true || workflowChatDisabledReason
                            ? "#E1E5EC"
                            : "#D8D3FF",
                        color: "#6846BF",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(15, 23, 42, 0.06)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                      },
                    }}
                  >
                    {promptText}
                  </Box>
                ))}
              </Box>
            </Box>
          );

          if (sortedMessages.length === 0) {
            return (
              <>
                {showAiOverview ? (
                  <AiOverview
                    compact={isEmbedded}
                    ticketId={ticketId}
                    ticketContext={aiOverviewTicketContext}
                    aiThreadId={aiThreadId}
                    fetchEnabled={chatEnabled === true}
                    onOverviewFetchResult={handleOverviewFetchResult}
                    fetchOverview={(tid, ctx, threadId) =>
                      chatApiInstance.fetchTicketAiOverview(tid, ctx, customerId, threadId)
                    }
                    hasProductivityApps={hasProductivityApps}
                    customerConnectedToTheo={customerConnectedToTheo}
                  />
                ) : null}
                {customWorkflowLifecycleCallout}
                {!hideNullStatePrompts && nullState}
              </>
            );
          }

          return (
            <>
              {showAiOverview ? (
                <AiOverview
                  compact={isEmbedded}
                  ticketId={ticketId}
                  ticketContext={aiOverviewTicketContext}
                  aiThreadId={aiThreadId}
                  fetchEnabled={chatEnabled === true}
                  onOverviewFetchResult={handleOverviewFetchResult}
                  fetchOverview={(tid, ctx, threadId) =>
                    chatApiInstance.fetchTicketAiOverview(tid, ctx, customerId, threadId)
                  }
                  hasProductivityApps={hasProductivityApps}
                  customerConnectedToTheo={customerConnectedToTheo}
                />
              ) : null}
              {customWorkflowLifecycleCallout}
              {renderComputerUseRuns(BEFORE_FIRST_MESSAGE)}
              {sortedMessages.map((msg: Message, index: number, array: Message[]) => {
            // Parse plan_progress from PLAN_PROGRESS_UPDATE content_type messages
            let planProgress = msg.plan_progress;
            if (msg.content_type === "PLAN_PROGRESS_UPDATE" && msg.content) {
              try {
                const parsed = JSON.parse(msg.content);
                planProgress = {
                  plan_description: parsed.plan_description || "",
                  completed_step_indices: parsed.completed_step_indices || [],
                  total_steps: parsed.total_steps || 0,
                };
              } catch (e) {
                console.error("Failed to parse plan_progress from message:", e);
              }
            }
            
            // Prefer entry_id when present so multiple HITLs in one turn (same message_id) get unique keys
            const entryId = (msg as Message & { entry_id?: string }).entry_id;
            const baseKey = entryId
              ? `entry-${entryId}`
              : `msg-${msg.message_id || "none"}-${msg.created_at?.seconds ?? "0"}-${msg.created_at?.nanos ?? 0}-${index}`;
            const messageKey = msg.isStreaming
              ? `${baseKey}-${(msg.content || "").length}`
              : baseKey;

            // Use cleanContent for all messages (including streaming) so markdown/lists render as chunks arrive.
            // With rAF batching we update at most ~60/sec so formatting cost is acceptable.
            const researchJobId = isNeoAssistantAuthor(msg.user_id)
              ? extractResearchJobId(msg.content)
              : null;
            const reportAttachments =
              !isNeoAssistantAuthor(msg.user_id)
                ? parseReportAttachmentsFromContent(msg.content)
                : [];
            const displayMessageContent = isNeoAssistantAuthor(msg.user_id)
              ? String(msg.content ?? "")
              : stripReportAttachmentTokensForDisplay(msg.content);
            const isThreadBadgeMessage = Boolean(threadAutomationBadgeId && msg.message_id === threadAutomationBadgeId);
            const automationModeBadge = !isNeoAssistantAuthor(msg.user_id)
              ? (
                  <AutoModeBadge
                    capacity={msg.automation_capacity || (isThreadBadgeMessage ? threadAutomationCapacity ?? undefined : undefined)}
                    agentName={
                      msg.agent_display_name ||
                      agentNameForId(msg.agent_id || (isThreadBadgeMessage ? threadAgentId : null))
                    }
                  />
                )
              : null;

            const bubblePres = getChatBubblePresentation(
              msg.user_id,
              userId,
              currentUser,
              true,
              agentDisplayNameForMessage(sortedMessages, index),
            );
            const renderedContent = (
              <div>
                <div
                  onClick={handleChoiceOptionClick}
                  dangerouslySetInnerHTML={{
                    __html: cleanContent(displayMessageContent),
                  }}
                />
                {canUseDeepResearch && researchJobId && aiThreadId ? (
                  <ResearchReportJobStatusInline
                    jobId={researchJobId}
                    aiThreadId={aiThreadId}
                    pollEnabled
                  />
                ) : null}
              </div>
            );

            return (
            <React.Fragment key={messageKey}>
              {msg.content_type === "HUMAN_REVIEW" ? (
                <ChatBubble
                  sender={bubblePres.chatBubbleSender}
                  content={
                    <div
                      onClick={handleChoiceOptionClick}
                      dangerouslySetInnerHTML={{
                        __html: cleanContent(msg.content ?? ""),
                      }}
                    />
                  }
                  timestamp={formatTimestamp(
                    msg.created_at?.seconds ||
                      `${Math.floor(Date.now() / 1000)}`
                  )}
                  senderName={bubblePres.senderName}
                  userAvatar={bubblePres.userAvatar}
                  userId={bubblePres.userIdLabel}
                  human_review={msg.human_review}
                  isHistory={
                    (msg.human_review_status === "approved" ||
                      msg.human_review_status === "canceled" ||
                      msg.human_review_status === "scheduled")
                      ? true
                      : index < array.length - 1
                  }
                  isLastMessage={index === array.length - 1}
                  human_review_status={msg.human_review_status}
                  message={msg}
                  messageId={msg.message_id}
                  currentUser={currentUser}
                  aiThreadId={aiThreadId || undefined}
                  customerId={customerId}
                  isEmbedded={isEmbedded}
                  plan_progress={planProgress}
                  pending={msg.pending}
                  status={msg.status || (msg.pending ? 'pending' : undefined)}
                  onOpenUndoModal={openUndoModalWithPreSelection}
                  onWorkflowTimerCanceled={() => {
                    void loadTicketSchedules();
                    void refreshTicketThreadAfterWorkflowResume();
                  }}
                />
              ) : (
                <ChatBubble
                  sender={bubblePres.chatBubbleSender}
                  content={renderedContent}
                  headerInlineContent={
                    reportAttachments.length > 0 ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {reportAttachments.map((attachment) => (
                          <MuiLink
                            key={`${msg.message_id || "msg"}-report-header-${attachment.jobId}`}
                            href={buildReportHref(attachment.jobId, null)}
                            target="_blank"
                            rel="noreferrer"
                            underline="none"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              border: "1px solid #D1D5DB",
                              backgroundColor: "#F9FAFB",
                              color: "#374151",
                              borderRadius: "999px",
                              px: 1,
                              py: 0.2,
                              fontSize: 11,
                              fontWeight: 600,
                              fontFamily: "var(--theo-font-sans)",
                              lineHeight: 1.2,
                              maxWidth: 420,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              "&:hover": {
                                backgroundColor: "#F3F4F6",
                              },
                            }}
                          >
                            {`report:${attachment.title}`}
                          </MuiLink>
                        ))}
                      </Box>
                    ) : null
                  }
                  automationModeBadge={automationModeBadge}
                  timestamp={formatTimestamp(
                    msg.created_at?.seconds ||
                      `${Math.floor(Date.now() / 1000)}`
                  )}
                  senderName={bubblePres.senderName}
                  userAvatar={bubblePres.userAvatar}
                  userId={bubblePres.userIdLabel}
                  human_review={msg.human_review}
                  human_review_status={msg.human_review_status}
                  isEmbedded={isEmbedded}
                  isLastMessage={index === array.length - 1}
                  message={msg}
                  messageId={msg.message_id}
                  status={(msg as any).status || (msg.pending ? 'pending' : undefined)}
                  pending={msg.pending}
                  ticketId={ticketId}
                  plan_progress={planProgress}
                  onWorkflowTimerCanceled={() => {
                    void loadTicketSchedules();
                    void refreshTicketThreadAfterWorkflowResume();
                  }}
                />
              )}
            {/* A browser run renders where it happened: after the message that asked for
                it, before the reply that reports what it did. */}
            {renderComputerUseRuns(index)}
            </React.Fragment>
            );
          })}
            </>
          );
        })()}
        {(isThinking || chatIsLoading) && !threadWait?.stop_turn && (
          <Box sx={{ display: "flex", justifyContent: "left", my: 1, ml: 4 }}>
            {isThinking ? (
              <ThinkingIndicator
                thinkingSteps={thinkingSteps}
                currentStep={currentThinkingStep || 0}
                isEmbedded={isEmbedded}
              />
            ) : (
              <TypingIndicator />
            )}
          </Box>
        )}
        <div ref={messagesEndRef} />
        </>
        )}
      </Box>
      )}

      {/* Input Section: Input, Pills, Prompts - only show if chat is enabled (true) */}
      {chatEnabled === true && (
        <Box
          sx={{
            width: "100%",
            p: 0,
            m: 0,
            flexShrink: 0,
          }}
        >
        {/* DISABLED: PillsBar — re-enable with pills logic above
        <Box sx={{ mt: 0.5, mb: 0.5 }}>
          <PillsBar
            selectedPill={selectedPill}
            onSelect={handlePillClick}
            pillCategories={filteredPills}
          />
        </Box>
        */}
        {/* Input and Prompts Container */}
        <Box sx={{ position: "relative" }}>
          {/* Typeahead above input */}
          <ClickAwayListener onClickAway={handleTypeaheadClose}>
            <Box
              sx={{
                position: "absolute",
                bottom: "100%",
                left: 0,
                width: "100%",
                zIndex: 25,
              }}
            >
                      <Typeahead
          inputText={inputMessage}
          debouncedInputText={debouncedInputText}
          onSelect={handleTypeaheadSelect}
          onClose={handleTypeaheadClose}
          visible={showTypeahead}
          customerId={customerId}
          mspId={userMspInfo?.msp_id?.toString()}
          ticketId={ticketId}
          onNavigationChange={handleTypeaheadNavigationChange}
        />
            </Box>
          </ClickAwayListener>
          
          {/* DISABLED: PromptsList — re-enable with pills logic above
          {showPrompts && selectedPill && selectedSuggestions.length > 0 && (
            <ClickAwayListener onClickAway={handleClosePrompts}>
              <Box
                sx={{
                  position: "absolute",
                  bottom: "100%",
                  left: 0,
                  width: "100%",
                  zIndex: 20,
                }}
              >
                <PromptsList
                  suggestions={selectedSuggestions}
                  onSelect={handleSuggestionClick}
                  onClose={handleClosePrompts}
                  inputRef={inputRef}
                  fontSizes={fontSizes}
                />
              </Box>
            </ClickAwayListener>
          )}
          */}
          <RemoteSessionInputSection
            inputMessage={inputMessage}
            setInputMessage={handleInputChange}
            chatIsLoading={Boolean(isThinking || chatIsLoading) && !interruptAutoRun}
            aiThreadId={aiThreadId}
            userId={userId}
            handleSendMessage={handleInputSend}
            queuedMessages={queuedMessages}
            onDeleteQueuedMessage={deleteQueuedMessage}
            onUpdateQueuedMessage={updateQueuedMessage}
            onSendQueuedMessageNow={sendQueuedMessageNow}
            canSendQueuedMessagesNow={canSendQueuedMessagesNow}
            onStopTurn={stopTurn}
            inputRef={inputRef}
            showTypeahead={showTypeahead}
            hasNavigatedToTypeahead={hasNavigatedToTypeahead}
            isEmbedded={isEmbedded}
            fontSizes={{ input: fontSizes.input }}
            modeControl={
              showTicketChatModeSelector ? (
                <TicketChatModeSelector
                  value={ticketChatMode}
                  agents={modeAgents}
                  threadMode={liveAutomationCapacity === 3 ? { capacity: 3, agentId: threadAgentId } : null}
                  onChange={setTicketChatMode}
                  disabled={chatEnabled !== true || Boolean(workflowChatDisabledReason)}
                />
              ) : null
            }
            disabledReason={chatDisabledReason}
            placeholder={chatReplyPlaceholder}
          />
        </Box>
      </Box>
      )}

      <Dialog
        open={showFileLinearTaskDialog}
        onClose={() => {
          if (!fileLinearTaskSubmitting) setShowFileLinearTaskDialog(false);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: "92vh" } }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 18,
            color: "#19213D",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddTaskIcon sx={{ color: "#2563EB" }} />
            <span>File Linear Task</span>
          </Box>
          <IconButton
            size="small"
            disabled={fileLinearTaskSubmitting}
            onClick={() => setShowFileLinearTaskDialog(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "32px !important", display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Title"
            value={fileLinearTaskTitle}
            onChange={(event) => setFileLinearTaskTitle(event.target.value)}
            fullWidth
            size="small"
            required
            autoFocus
            sx={{ mt: 0.5 }}
          />
          <TextField
            label="Description"
            value={fileLinearTaskDescription}
            onChange={(event) => setFileLinearTaskDescription(event.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={4}
            placeholder="Add context for the Linear task"
            helperText="Optional extra context to include in the Linear task description."
          />
          <TextField
            label="MSP"
            value={fileLinearTaskMspLabel || "MSP info unavailable"}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            helperText="This MSP info is included after the description in the Linear task."
          />
          <TextField
            label="Ticket link"
            value={fileLinearTaskTicketLink}
            onChange={(event) => setFileLinearTaskTicketLink(event.target.value)}
            fullWidth
            size="small"
            helperText="This link is included separately in the Linear task."
          />
          <TextField
            label="Associated LangSmith trace link"
            value={fileLinearTaskTraceUrl}
            onChange={(event) => setFileLinearTaskTraceUrl(event.target.value)}
            fullWidth
            size="small"
            placeholder={fileLinearTaskTraceLoading ? "Loading latest trace..." : "Latest chat trace will be attached automatically if left blank"}
            InputProps={{
              endAdornment: fileLinearTaskTraceLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={18} />
                </InputAdornment>
              ) : null,
            }}
            helperText={
              fileLinearTaskTraceLoading
                ? "Looking up the latest LangSmith trace for this thread..."
                : "Leave blank to let Cortex attach the latest trace during task creation."
            }
          />
          <LayeredAutocompleteSelect
            label="Assigned to"
            value={fileLinearTaskAssignee}
            onChange={value => setFileLinearTaskAssignee(value)}
            fullWidth
            size="small"
            disabled={fileLinearTaskAssigneesLoading}
            helperText={
              fileLinearTaskAssigneesLoading
                ? "Loading Linear team members..."
                : "Members are loaded from the configured Linear THEOP team."
            }
            disableClearable
            options={fileLinearTaskAssigneeOptions.map(assignee => ({ value: assignee.email, label: assignee.name + (assignee.email ? " (" + assignee.email + ")" : "") }))}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setShowFileLinearTaskDialog(false)}
            disabled={fileLinearTaskSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitFileLinearTask}
            disabled={fileLinearTaskSubmitting || !fileLinearTaskTitle.trim() || !aiThreadId}
            startIcon={fileLinearTaskSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              backgroundColor: "#2563EB",
              "&:hover": { backgroundColor: "#1D4ED8" },
            }}
          >
            Create Task
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(createdLinearTask)}
        onClose={() => setCreatedLinearTask(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 18,
            color: "#19213D",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddTaskIcon sx={{ color: "#2563EB" }} />
            <span>Linear Task Created</span>
          </Box>
          <IconButton
            aria-label="Close Linear task created dialog"
            size="small"
            onClick={() => setCreatedLinearTask(null)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: "Inter", fontSize: 14, color: "#19213D", mb: 1 }}>
            {createdLinearTask?.identifier || "Linear task"} was created successfully.
          </Typography>
          {createdLinearTask?.url ? (
            <MuiLink
              href={createdLinearTask.url}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontFamily: "Inter", fontSize: 14, fontWeight: 600, color: "#2563EB" }}
            >
              Open {createdLinearTask.identifier || createdLinearTask.title}
            </MuiLink>
          ) : null}
        </DialogContent>
      </Dialog>

      <DeleteModal
        open={showDeleteModal}
        onCancel={() => setShowDeleteModal(false)}
        description="Are you sure you want to delete this chat thread?"
        warning={deleteThreadWarning(
          deletePendingScheduleCount > 0
            ? `${deletePendingScheduleCount} pending schedule${deletePendingScheduleCount === 1 ? "" : "s"} for this ticket will also be canceled.`
            : null,
          threadWait,
        )}
        onDelete={async () => {
          if (!aiThreadId || !ticketId) return;
          try {
            await dispatch(ChatAction.resetAiThread(aiThreadId, ticketId));
            setDeleteError(null);
            setShowDeleteSnackbar(true);
          } catch (error) {
            setDeleteError("An error occurred while resetting the chat.");
            setShowDeleteSnackbar(true);
          }
        }}
      />

      <ToolAuditLogPopup
        open={auditLogPopupOpen}
        onClose={() => setAuditLogPopupOpen(false)}
        aiThreadId={aiThreadId ?? null}
        ticketId={ticketId ?? null}
        onLiveDebug={() => {
          setLiveDebug(true);
          setAuditLogPopupOpen(false);
        }}
      />
      {liveDebug && chatEnabled === true && (
        <LiveDebugPanel
          aiThreadId={aiThreadId}
          messages={messages}
          isThinking={isThinking}
          onClose={() => setLiveDebug(false)}
        />
      )}

      {aiThreadId && (
        <UndoActionsModal
          open={undoModalOpen}
          onClose={() => { setUndoModalOpen(false); setUndoPreSelected([]); }}
          aiThreadId={aiThreadId}
          userEmail={String(currentUser?.email ?? "")}
          preSelectedCallIds={undoPreSelected}
        />
      )}

      <Snackbar
        open={showDeleteSnackbar}
        autoHideDuration={1500}
        onClose={() => {
          setShowDeleteSnackbar(false);
          setDeleteError(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => {
            setShowDeleteSnackbar(false);
            setDeleteError(null);
          }}
          severity={deleteError ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {deleteError ? deleteError : "Chat history deleted successfully."}
        </MuiAlert>
      </Snackbar>

      <Snackbar
        open={fileLinearTaskSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setFileLinearTaskSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setFileLinearTaskSnackbar((prev) => ({ ...prev, open: false }))}
          severity={fileLinearTaskSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {fileLinearTaskSnackbar.message}
        </MuiAlert>
      </Snackbar>

      {/* Workflow Selection Dialog */}
      <Dialog
        open={showWorkflowDialog}
        onClose={() => {
          setShowWorkflowDialog(false);
          setWorkflowDialogLifecycleFilter(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            minHeight: "300px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "var(--theo-font-sans)",
            fontWeight: 600,
            fontSize: 18,
            color: "#19213D",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountTreeIcon sx={{ color: "#9B7AEF" }} />
              <span>Select Workflow</span>
            </Box>
            {workflowDialogLifecycleFilter ? (
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 13,
                  fontWeight: 400,
                  color: "#666F8D",
                  pl: 4,
                }}
              >
                {lifecycleToCustomWorkflowType(workflowDialogLifecycleFilter)} workflows for this
                customer
              </Typography>
            ) : null}
          </Box>
          <IconButton
            size="small"
            onClick={() => {
              setShowWorkflowDialog(false);
              setWorkflowDialogLifecycleFilter(null);
            }}
            sx={{
              color: "#666F8D",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {workflowsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : workflows.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "200px",
                p: 3,
              }}
            >
              <AccountTreeIcon
                sx={{ fontSize: 48, color: "#E5E7EB", mb: 2 }}
              />
              <Typography
                sx={{
                  fontFamily: "var(--theo-font-sans)",
                  fontSize: 16,
                  color: "#666F8D",
                  textAlign: "center",
                }}
              >
                {workflowDialogLifecycleFilter
                  ? `No ${lifecycleToCustomWorkflowType(workflowDialogLifecycleFilter)} workflows available for this customer`
                  : "No workflows available for this customer"}
              </Typography>
              {canUseCustomWorkflows ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: 14,
                      color: "#666F8D",
                      textAlign: "center",
                      mt: 1.5,
                      maxWidth: 360,
                      lineHeight: 1.5,
                    }}
                  >
                    Create one in{" "}
                    <MuiLink
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => {
                        setShowWorkflowDialog(false);
                        setWorkflowDialogLifecycleFilter(null);
                        navigateCustomWorkflows();
                      }}
                      sx={{
                        fontFamily: "inherit",
                        fontSize: "inherit",
                        fontWeight: 600,
                        color: "#9B7AEF",
                        verticalAlign: "baseline",
                      }}
                    >
                      Custom Workflows
                    </MuiLink>
                    : open it from the sidebar, click{" "}
                    <Box component="span" sx={{ fontWeight: 600, color: "#19213D" }}>
                      Add Custom Workflow
                    </Box>
                    , configure your steps, and assign the workflow to this customer.
                  </Typography>
                  <NeoButton
                    variant="neo"
                    onClick={() => {
                      setShowWorkflowDialog(false);
                      setWorkflowDialogLifecycleFilter(null);
                      navigateCustomWorkflows();
                    }}
                    sx={{ mt: 2.5 }}
                  >
                    Go to Custom Workflows
                  </NeoButton>
                </>
              ) : null}
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {/* Select Workflow list: Play runs now; gear opens Configure. Both disabled while a step wait locks chat. */}
              {workflows.map((workflow, index) => {
                const autoContinueHitl = workflowAutoContinueHitlEnabled(workflow);
                return (
                  <ListItem
                  key={workflow.id}
                  secondaryAction={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mr: 1 }}>
                      <Tooltip title={workflowChatDisabledReason || "Run workflow"}>
                        <span>
                          <IconButton
                            edge="end"
                            aria-label="Run workflow"
                            disabled={Boolean(workflowChatDisabledReason) || chatIsLoading}
                            onClick={() => handleSelectWorkflow(workflow)}
                            sx={{
                              width: 40,
                              height: 40,
                              color: "#705D4A",
                            }}
                          >
                            <PlayArrowIcon sx={{ fontSize: 22 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {schedulerEnabled && workflow.is_custom && ticketId ? (
                        <Tooltip title={workflowChatDisabledReason || "Configure workflow"}>
                          <span>
                            <IconButton
                              edge="end"
                              aria-label="Configure workflow"
                              disabled={Boolean(workflowChatDisabledReason) || chatIsLoading}
                              onClick={(event) => handleOpenWorkflowSchedule(event, workflow)}
                              sx={{
                                width: 40,
                                height: 40,
                                color: "#705D4A",
                              }}
                            >
                              <SettingsIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                    </Box>
                  }
                  sx={{
                    py: 1.5,
                    px: 3,
                    pr: schedulerEnabled && workflow.is_custom && ticketId ? 16 : 10,
                    borderBottom:
                      index < workflows.length - 1
                        ? "1px solid #F0F2F5"
                        : "none",
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <span>{workflow.workflow_name}</span>
                        {autoContinueHitl ? (
                          <Chip
                            label="Auto-continue HITL"
                            size="small"
                            sx={{
                              height: 21,
                              bgcolor: "#F1ECFF",
                              color: "#6941C6",
                              fontFamily: "var(--theo-font-sans)",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          />
                        ) : null}
                      </Box>
                    }
                    primaryTypographyProps={{
                      component: "div",
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: fontSizes.body,
                      fontWeight: 500,
                      color: "#19213D",
                    }}
                    secondary={
                    getWorkflowStepSummary(workflow) ??
                    (workflow.guides && workflow.guides.length > 0
                      ? `${workflow.guides.length} guide${
                          workflow.guides.length === 1 ? "" : "s"
                        }`
                      : "No guides")
                    }
                    secondaryTypographyProps={{
                      fontFamily: "var(--theo-font-sans)",
                      fontSize: fontSizes.small,
                      color: "#666F8D",
                    }}
                  />
                </ListItem>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <ScheduleWorkflowDialog
        open={Boolean(workflowToSchedule)}
        workflowName={workflowToSchedule?.workflow_name || ""}
        workflowStepsJson={workflowToSchedule?.args_json}
        submitting={workflowScheduleSubmitting}
        error={workflowScheduleError}
        onClose={() => {
          if (workflowScheduleSubmitting) return;
          setWorkflowToSchedule(null);
          setWorkflowScheduleError(null);
        }}
        onConfirm={handleConfirmWorkflowSchedule}
      />

      <Snackbar
        open={workflowScheduleSnackbar.open}
        autoHideDuration={4000}
        onClose={() => setWorkflowScheduleSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setWorkflowScheduleSnackbar((prev) => ({ ...prev, open: false }))}
          severity={workflowScheduleSnackbar.severity}
          sx={{ width: "100%" }}
        >
          {workflowScheduleSnackbar.message}
        </MuiAlert>
      </Snackbar>

      <Dialog
        open={Boolean(screenshotLightboxSrc)}
        onClose={() => setScreenshotLightboxSrc(null)}
        maxWidth="xl"
        fullWidth
      >
        <DialogContent sx={{ p: 1, position: "relative" }}>
          <IconButton
            aria-label="Close screenshot"
            onClick={() => setScreenshotLightboxSrc(null)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "background.paper",
            }}
          >
            <CloseIcon />
          </IconButton>
          {screenshotLightboxSrc ? (
            <Box
              component="img"
              src={screenshotLightboxSrc}
              alt="Enlarged desktop screenshot"
              sx={{ width: "100%", height: "auto", display: "block", maxHeight: "90vh", objectFit: "contain" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default NeoAssistant;
